import { useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { inputClass } from "./classes";
import { useFrozenBand } from "./sticky";

/** Everything a row knows, lowercased, so a filter term matches wherever it appears — displayed or not. */
function rowText(row: unknown): string {
  const parts: string[] = [];
  const walk = (v: unknown): void => {
    if (v === null || v === undefined) return;
    if (typeof v === "object") Object.values(v as Record<string, unknown>).forEach(walk);
    else parts.push(String(v));
  };
  walk(row);
  return parts.join(" ").toLowerCase();
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  /** Right-aligned, tabular figures. */
  numeric?: boolean;
  /** Sortable by this value; the header becomes a button with aria-sort. */
  sortValue?: (row: T) => string | number;
}

type Sort = { key: string; dir: "ascending" | "descending" };

/** The filter row, frozen under the page's banner; its own component so its height is measured from the moment it mounts. */
function FilterRow({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const frozen = useFrozenBand(ref, "table-bar", "parent");
  return (
    <div ref={ref} className={`flex flex-wrap items-center justify-between gap-3 bg-[var(--band-bg,var(--color-canvas))] pb-3 ${frozen ? "sticky top-[calc(var(--shell-bar,0px)+var(--page-bar,0px))] z-10" : ""}`}>
      {children}
    </div>
  );
}

/**
 * The table's region scrolls sideways only when the table is wider than it
 * — measured, not guessed from a breakpoint. Mermera's port scrolled below
 * `lg` and froze above it, but a table that is still too wide at `lg` (the
 * audit trail at x-large text) then pushed the whole page sideways (ACC-07).
 * Measuring keeps both: a table that fits never scrolls, so its titles
 * freeze; one that does not scrolls in its region, never the page.
 */
function ScrollRegion({ caption, children }: { caption: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [scrolls, setScrolls] = useState(false);
  useLayoutEffect(() => {
    const region = ref.current;
    const table = region?.firstElementChild;
    if (!region || !table) return;
    const measure = () => setScrolls(table.scrollWidth > region.clientWidth + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(region);
    ro.observe(table);
    return () => ro.disconnect();
  }, []);
  return (
    // A scrollable region must be reachable by keyboard to scroll it sideways (WCAG 2.1.1); the label tells a screen
    // reader what it is — the one place a landmark takes a tabindex. `relative` makes it the containing block for the
    // sr-only spans inside (RelativeTime's absolute time): an absolutely positioned element inside a scroll container
    // otherwise sizes the PAGE, not the region.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    <div ref={ref} className={`relative ${scrolls ? "overflow-x-auto" : ""}`} data-scrolls={scrolls ? "true" : "false"} role="region" aria-label={caption} tabIndex={0}>
      {children}
    </div>
  );
}

/**
 * Every list (mermera's shared-ui DataTable, with sorting made
 * keyboard-first): a real table with a caption, `scope` on headers, a
 * filter box that matches anywhere in the row, and sortable columns whose
 * header is a button that announces the order through aria-sort. The
 * caption is visible unless `captionHidden`; a screen reader always
 * gets it. On a phone the table scrolls sideways inside a labelled region
 * rather than the page.
 *
 * While the rows scroll, the filter row stays under the page's banner and
 * the column titles under the filter row (owner, 2026-09-21; gairm/lms#215):
 * the filter row sticks at --page-bar and publishes its own height as
 * --table-bar; each <th> sticks at the sum. Both paint --band-bg (the
 * canvas, or a Card's surface) so rows do not show through. The table is
 * `border-separate` because a collapsed border does not travel with a stuck
 * cell. The titles freeze only while the table fits: a table wider than
 * its column scrolls sideways inside its region (below), and a box that
 * scrolls in one axis is a scroll container in both, which a sticky <th>
 * sticks to instead of the page — so there the titles ride with the rows.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  caption,
  captionHidden = false,
  empty,
  filter = true,
  initialSort,
  rowTestId,
}: {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  caption: string;
  captionHidden?: boolean | undefined;
  empty: ReactNode;
  filter?: boolean | undefined;
  initialSort?: Sort | undefined;
  rowTestId?: ((row: T) => string) | undefined;
}) {
  const [term, setTerm] = useState("");
  const [sort, setSort] = useState<Sort | undefined>(initialSort);
  const inputId = useId();
  if (rows.length === 0) return <>{empty}</>;

  const needle = term.trim().toLowerCase();
  let visible = needle ? rows.filter((r) => rowText(r).includes(needle)) : rows;
  const sortCol = sort ? columns.find((c) => c.key === sort.key) : undefined;
  if (sort && sortCol?.sortValue) {
    const value = sortCol.sortValue;
    visible = [...visible].sort((a, b) => collator.compare(String(value(a)), String(value(b))) * (sort.dir === "ascending" ? 1 : -1));
  }

  const toggleSort = (key: string) =>
    setSort((prev) => (prev?.key === key ? { key, dir: prev.dir === "ascending" ? "descending" : "ascending" } : { key, dir: "ascending" }));

  return (
    <div>
      {filter && rows.length > 1 && (
        <FilterRow>
          <label htmlFor={inputId} className="sr-only">
            Filter {caption}
          </label>
          <input id={inputId} type="search" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Filter" className={`${inputClass} w-64 max-w-full`} />
          <span className="text-xs text-muted" aria-live="polite">
            {needle ? `${visible.length} of ${rows.length} shown` : ""}
          </span>
        </FilterRow>
      )}
      {visible.length === 0 ? (
        <p className="py-6 text-sm text-muted">Nothing matches “{term.trim()}”.</p>
      ) : (
        <ScrollRegion caption={caption}>
          <table className="w-full border-separate border-spacing-0 text-sm">
            <caption className={captionHidden ? "sr-only" : "mb-2 text-left text-sm text-muted"}>{caption}</caption>
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted">
                {columns.map((c) => {
                  const active = sort?.key === c.key;
                  return (
                    <th key={c.key} scope="col" aria-sort={c.sortValue ? (active ? sort!.dir : "none") : undefined} className={`border-b border-line bg-[var(--band-bg,var(--color-canvas))] py-2 pr-4 font-medium freeze:sticky freeze:top-[calc(var(--shell-bar,0px)+var(--page-bar,0px)+var(--table-bar,0px))] freeze:z-[5] [[data-scrolls=true]_&]:static! ${c.numeric ? "text-right" : ""}`}>
                      {c.sortValue ? (
                        <button type="button" onClick={() => toggleSort(c.key)} className="inline-flex min-h-6 items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted underline-offset-2 hover:underline">
                          {c.header}
                          <span aria-hidden="true">{active ? (sort!.dir === "ascending" ? "▲" : "▼") : "↕"}</span>
                        </button>
                      ) : (
                        c.header
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr key={rowKey(row)} data-testid={rowTestId?.(row)} className="group">
                  {columns.map((c) => (
                    <td key={c.key} className={`border-b border-line py-3 pr-4 align-middle group-last:border-0 ${c.numeric ? "text-right tabular-nums" : ""}`}>
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollRegion>
      )}
    </div>
  );
}
